import { isPremiumTier } from '@/lib/tier';
import { useAuth } from '@/hooks/useAuth';
import { FEATURE_FLAGS } from '@/config/features';

interface FeatureAccessResult {
  hasAccess: boolean;
  isLoading: boolean;
}

/**
 * Check if current user has access to vocabulary lists feature
 * Returns { hasAccess: true } if feature flag is enabled OR user is admin
 * Returns { isLoading: true } while tier is being determined
 */
export function useVocabularyListsAccess(): FeatureAccessResult {
  const { tier, tierInitialized } = useAuth();
  
  // If feature flag is enabled, access is granted regardless of tier
  if (FEATURE_FLAGS.VOCABULARY_LISTS_ENABLED) {
    return { hasAccess: true, isLoading: false };
  }
  
  // Wait for tier to be initialized before checking admin status
  if (!tierInitialized) {
    return { hasAccess: false, isLoading: true };
  }
  
  return { hasAccess: tier === 'admin', isLoading: false };
}

/**
 * Check if current user has access to ElevenLabs STT fallback (RFC 041 / RFC 084)
 * Native STT is available to ALL authenticated users on native — this hook
 * only gates the ElevenLabs Scribe fallback for unsupported languages.
 * When STT_PREMIUM_ENABLED is false: admin only
 * When STT_PREMIUM_ENABLED is true: premium, paid, and admin
 */
export function useSpeechToTextAccess(): FeatureAccessResult {
  const { tier, tierInitialized } = useAuth();
  
  if (!tierInitialized) {
    return { hasAccess: false, isLoading: true };
  }
  
  // Admin always has access
  if (tier === 'admin') {
    return { hasAccess: true, isLoading: false };
  }
  
  // Premium-equivalent (incl. teacher) only when flag is enabled
  if (FEATURE_FLAGS.STT_PREMIUM_ENABLED && isPremiumTier(tier)) {
    return { hasAccess: true, isLoading: false };
  }
  
  return { hasAccess: false, isLoading: false };
}

/**
 * Check if current user has access to Smart Scan (RFC 044)
 * Premium, paid, and admin users have access — no feature flag needed.
 */
export function useSmartScanAccess(): FeatureAccessResult {
  const { tier, tierInitialized } = useAuth();
  
  if (!tierInitialized) {
    return { hasAccess: false, isLoading: true };
  }
  
  const hasAccess = isPremiumTier(tier);
  return { hasAccess, isLoading: false };
}
